<template>
  <div>
    <div class="mb-4">
      <slot name="header" />
    </div>

    <v-file-input
      v-if="formField.inputType == 'file'"
      v-model="inputValue"
      :label="label"
      :hint="hint"
      :accept="accept"
      persistent-hint
      :error-messages="errorMessages"
      :disabled="disabled"
      variant="outlined"
      :data-test="formField.name"
      @blur="validation.$touch()"
    />
    <AppFileUploadMultiple
      v-else-if="formField.inputType == 'multifile'"
      v-model="inputValue"
      :label="label"
      :hint="hint"
      :accept="accept"
      persistent-hint
      :data-test="formField.name"
      :error-messages="errorMessages"
      :disabled="disabled"
      @blur="validation.$touch()"
    />

    <template v-if="uploadedDocuments.length > 0">
      <table class="entsys--table no-header" data-test="uploadedDocuments">
        <colgroup>
          <col style="width: 1px" />
          <col style="width: 1px" />
          <col />
        </colgroup>
        <tbody>
          <tr v-for="doc in uploadedDocuments" :key="doc.id">
            <td>
              <v-checkbox-btn
                v-model="removeFileInputValue"
                :value="doc.id"
                color="secondary"
                multiple
                true-icon="$checkboxIndeterminate"
              />
            </td>
            <td style="position: relative">
              <v-icon size="small" icon="$iconPage" />
            </td>
            <td>
              <div>
                <a :href="prependApiDomain(doc.path)" target="_blank">
                  <b>
                    {{ doc.filename }}
                  </b>
                </a>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <br />
      <br />
    </template>
  </div>
</template>
<script>
import { defineComponent, computed, onBeforeMount } from "vue";
import { useTranslation } from "i18next-vue";
import gql from "fraql";
import { prependApiDomain } from "@/utils/prependApiDomain";
import AppFileUploadMultiple from "./AppFileUploadMultiple.vue";

export default defineComponent({
  name: "AppFileUploadSkeleton",
  components: {
    AppFileUploadMultiple
  },
  props: {
    modelValue: {
      type: [Object, Array],
      default: null
    },
    uploadedDocuments: {
      type: Array,
      default: () => []
    },
    toRemoveIds: {
      type: Array,
      default: () => []
    },
    formField: {
      type: Object,
      required: true
    },
    validation: {
      type: Object,
      required: true
    },
    errorMessages: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    accept: {
      type: String,
      default: null
    }
  },
  emits: ["update:modelValue", "update:toRemoveIds"],
  setup(props, ctx) {
    const { t } = useTranslation();

    const fieldName = computed(() => props.formField.name);

    const label = computed(() =>
      t(props.formField.labelTranslationKey, props.formField.label)
    );
    const hint = computed(() =>
      t(props.formField.hintTranslationKey, props.formField.hint)
    );

    const inputValue = computed({
      get() {
        return props.modelValue;
      },
      set(v) {
        ctx.emit("update:modelValue", v);
      }
    });

    const removeFileInputValue = computed({
      get() {
        return props.toRemoveIds;
      },
      set(v) {
        ctx.emit("update:toRemoveIds", v);
      }
    });

    onBeforeMount(() => {
      if (
        inputValue.value == null &&
        props.formField.inputType == "multifile"
      ) {
        inputValue.value = [];
      }
    });

    return {
      prependApiDomain,
      fieldName,
      label,
      hint,
      inputValue,
      removeFileInputValue
    };
  },
  fragments: {
    formFields: gql`
      fragment _ on FormField {
        name
        entity
        inputType
        dataType
        label
        labelTranslationKey
        hint
        hintTranslationKey
      }
    `,
    documentFields: gql`
      fragment _ on AttachedFile {
        id
        filename
        path
      }
    `
  }
});

/**
 * Function that returns formFields only with UploadableFile entities
 * @param {*} formFields
 */
export function filterUploadableInputs(formFields) {
  return formFields.filter(formField => formField.entity == "UploadableFile");
}

/**
 * Function that moves documents to *Uploaded field and creates *ToRemoveIds array field
 * @param {*} formField
 * @param {*} data object where to get data by formField.name
 * @param {Function} setter function that sets new *Uploaded and *ToRemoveIds fields
 */
export function setUploadableInputFields(formField, data, setter) {
  setter(formField.name, formField.inputType == "multifile" ? [] : null);

  const docs = data?.[formField.name] ?? [];
  setter(formField.name + "Uploaded", docs);
  setter(formField.name + "ToRemoveIds", []);
}

/**
 * Function that replaces required to requiredIf that depends on *Uploaded and *ToRemoveIds fields.
 * requiredIf returning true means required to validate and false not to validate
 * @param {*} validation validation for an field
 * @param {*} formField
 * @param {*} siblings typical form object
 */
export function replaceRequiredFileToRequiredIf(
  validation,
  formField,
  siblings
) {
  if (!validation.required) return validation;

  delete validation.required;

  const uploaded = formField.name + "Uploaded";
  const toRemoveIds = formField.name + "ToRemoveIds";
  if (formField.inputType == "file") {
    validation.requiredIf = () => {
      return siblings[formField.name] || siblings[uploaded].length > 0
        ? siblings[toRemoveIds].length > 0 // if uploaded is marked to deletion, return true to give error on needing to have uploaed file
          ? true
          : false
        : true;
    };
  } else if (formField.inputType == "multifile") {
    validation.requiredIf = () => {
      return siblings[formField.name].length > 0 ||
        siblings[uploaded].length > 0
        ? siblings[uploaded].length == siblings[toRemoveIds].length // if to be be removed files length is same as uploaded, return true to validate as it is required to have file
          ? true
          : false
        : true;
    };
  }

  return validation;
}
</script>
